import { ReactNode, useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import { Link } from '@mui/material';

// theme
import { bgBlur } from 'src/theme/css';
import CompassLogo from 'src/assets/compass-logo.jpg';
// hooks
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
// components
import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import { useSettingsContext } from 'src/components/settings';
import { useSiteProperties } from 'src/compass/application';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import { Colors } from 'src/compass/shared/util/Colors';
import { ColorsNonProd } from 'src/compass/shared/util/ColorsNonProd';
import { useCasAuthenticated } from 'src/compass/shared/hooks/useCasAuthenticated';
//
import { HEADER, NAV } from 'src/layouts/config-layout';
import CasAccountPopover from './CasAccountPopover';
import CasEnvironmentDialog from './CasEnvironmentDialog';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function CasHeader({ onOpenNav }: Props) {
  const theme = useTheme();
  const {isAuthenticated} = useCasAuthenticated();

  const settings = useSettingsContext();

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  const lgUp = useResponsive('up', 'lg');

  const offset = useOffSetTop(HEADER.H_DESKTOP);

  const offsetTop = offset && !isNavHorizontal;

  const { data, isProductionDatabase, isLoading, isError, documentation, attachmentUrl,
    attachmentFilename, storyUrl, hasEnvironmentInfo } = useSiteProperties();

  const [environmentDialogOpen, setEnvironmentDialogOpen] = useState(false);

  let label: string | ReactNode = '';
  if (isLoading) label = <CasLoader />;
  else if (isError) label = 'Error';
  else label = data?.databaseName || '(DatabaseName not specified)';

  // Local dev only: several worktrees run side by side against the same database, so the database
  // chip alone doesn't identify which branch a window belongs to. Written to .env.local by the
  // prestart script; the NODE_ENV check is stripped from production builds.
  const branchName = process.env.REACT_APP_BRANCH_NAME || '';
  const showBranchChip = process.env.NODE_ENV === 'development' && branchName !== '';

  const renderContent = (
    <>
      {lgUp && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!lgUp && isAuthenticated && (
        <IconButton className="header-menu-button no-print" onClick={onOpenNav}>
          <SvgColor src="/assets/icons/navbar/ic_menu_item.svg" />
        </IconButton>
      )}

      <img src={CompassLogo} alt="Compass Ag Solutions" id="compass-logo" />

      <Link href="tel:+18664334350" id="company-phone">
        866-433-4350
      </Link>

      {/* On a provisioned environment's site the tag is the way into that
          environment's notes. Everywhere else - dev2, production, ordinary
          local dev - the API returns no documentation, attachment or story, so
          it stays the plain label it has always been. */}
      {!isProductionDatabase && (
        <Chip
          label={label}
          onClick={hasEnvironmentInfo ? () => setEnvironmentDialogOpen(true) : undefined}
          title={hasEnvironmentInfo ? 'Show this environment’s documentation' : undefined}
          sx={{
            backgroundColor: Colors.Present,
            color: 'black',
            fontWeight: 'bold',
            ml: 2,
            fontSize: 15,
            // A clickable Chip picks up a palette-derived hover background that
            // would fight the explicit backgroundColor above, so pin it.
            '&:hover': { backgroundColor: Colors.Present },
          }}
        />
      )}

      {showBranchChip && (
        <Chip
          label={branchName.split('/').pop()}
          title={branchName}
          sx={{
            backgroundColor: Colors.Future,
            color: 'black',
            fontWeight: 'bold',
            ml: 1,
            fontSize: 15,
          }}
        />
      )}

      {isAuthenticated && (
        <Stack
          className="no-print"
          flexGrow={1}
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={{ xs: 0.5, sm: 1 }}
        >
          <CasAccountPopover />
        </Stack>
      )}
    </>
  );

  return (
    <AppBar
      sx={{
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(lgUp && {
          width: isAuthenticated ? `calc(100% - ${NAV.W_VERTICAL + 1}px)` : '100%',
          height: HEADER.H_DESKTOP,
          ...(offsetTop && {
            height: HEADER.H_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: isAuthenticated ? `calc(100% - ${NAV.W_MINI + 1}px)` : '100%',
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
          bgcolor: isProductionDatabase ? 'inherit' : ColorsNonProd.Main,
        }}
      >
        <>
          <div id="toolbar-inside-border" />
          {renderContent}
          {environmentDialogOpen && (
            <CasEnvironmentDialog
              open
              onClose={() => setEnvironmentDialogOpen(false)}
              title={typeof label === 'string' ? label : ''}
              documentation={documentation}
              attachmentUrl={attachmentUrl}
              attachmentFilename={attachmentFilename}
              storyUrl={storyUrl}
            />
          )}
        </>
      </Toolbar>
    </AppBar>
  );
}
